import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id);
  const [collected, setCollected] = useState(false);
  const [showAll, setShowAll] = useState(false);

  if (!comic) {
    return (
      <div className="bg-background-50">
        <Navbar />
        <main className="flex min-h-[70vh] flex-col items-center justify-center gap-4 pt-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl text-foreground-900">没有找到这部漫画</h1>
          <Link to="/" className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);
  const chaptersToShow = showAll ? comic.chapters : comic.chapters.slice(0, 6);

  return (
    <div className="bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <nav aria-label="面包屑" className="mb-6 text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li>
                <Link to={`/category/${comic.category === "萌系漫画" ? "mengxi" : comic.category === "甜宠漫画" ? "tianchong" : "lianai"}`} className="hover:text-primary-600">
                  {comic.category}
                </Link>
              </li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li aria-current="page" className="text-foreground-800">{comic.title}</li>
            </ol>
          </nav>

          <section className="grid grid-cols-1 gap-8 md:grid-cols-[280px_1fr] lg:gap-12">
            <div className="mx-auto w-full max-w-[280px] md:mx-0">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 萌系漫画封面`}
                  title={`${comic.title} 少女漫画在线看`}
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-3xl text-foreground-900 md:text-4xl">{comic.title}</h1>
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ${
                    comic.status === "已完结"
                      ? "bg-secondary-100 text-secondary-800"
                      : "bg-primary-100 text-primary-700"
                  }`}
                >
                  {comic.status}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-600">
                    #{t}
                  </span>
                ))}
              </div>

              <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-600">{comic.intro}</p>

              <div className="mt-6 grid max-w-md grid-cols-3 gap-4 border-y border-background-200 py-4 text-center">
                <div>
                  <p className="text-xl font-medium text-foreground-900">{(comic.views / 10000).toFixed(1)}万</p>
                  <p className="mt-1 text-xs text-foreground-500">人气</p>
                </div>
                <div>
                  <p className="text-xl font-medium text-foreground-900">{comic.episodes}话</p>
                  <p className="mt-1 text-xs text-foreground-500">已更新</p>
                </div>
                <div>
                  <p className="text-xl font-medium text-foreground-900">{comic.rating}分</p>
                  <p className="mt-1 text-xs text-foreground-500">读者评分</p>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <a
                  href="#chapters"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-center text-base font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  开始阅读
                </a>
                <button
                  onClick={() => setCollected((v) => !v)}
                  className={`whitespace-nowrap rounded-full border px-7 py-3 text-base transition-colors ${
                    collected
                      ? "border-accent-300 bg-accent-100 text-accent-700"
                      : "border-background-300 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className={`${collected ? "ri-heart-fill" : "ri-heart-line"} mr-1`} />
                  {collected ? "已收藏" : "加入收藏"}
                </button>
              </div>

              <p className="mt-4 text-sm text-foreground-400">
                作者：<span className="text-foreground-600">{comic.author}</span> · 更新：{comic.updateTime} · 收藏 {comic.likes}
              </p>
            </div>
          </section>
        </div>

        <section id="chapters" className="bg-background-100 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-2xl text-foreground-900">章节列表</h2>
                <p className="mt-1 text-sm text-foreground-500">共 {comic.episodes} 话，点击即可开始阅读</p>
              </div>
              <button
                onClick={() => setShowAll((v) => !v)}
                className="whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-50"
              >
                {showAll ? "收起目录" : "查看全部目录"}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {chaptersToShow.map((ch) => (
                <a
                  key={ch.id}
                  href="#chapters"
                  className="group flex items-center justify-between rounded-xl bg-background-50 px-4 py-3 transition-colors hover:bg-primary-50"
                >
                  <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                  <span className="flex items-center gap-2 text-xs text-foreground-400">
                    <i className="ri-eye-line" />
                    {ch.views > 10000 ? `${(ch.views / 10000).toFixed(1)}万` : ch.views}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-heading text-2xl text-foreground-900">相关推荐</h2>
              <Link to="/category/all" className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700">
                更多同类
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ComicSeries",
            name: comic.title,
            url: `https://adorabook.github.io/comic/${comic.id}`,
            description: comic.intro,
            author: { "@type": "Person", name: comic.author },
            keywords: comic.tags.join(", "),
            numberOfEpisodes: comic.episodes,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://adorabook.github.io/" },
                { "@type": "ListItem", position: 2, name: comic.category },
                { "@type": "ListItem", position: 3, name: comic.title },
              ],
            },
          }),
        }}
      />
    </div>
  );
}